import { useQuery } from 'react-query';
import { Portfolio } from '../../types/Portfolio';
import { buildEndpoint } from '../endpoints';
import { RiskProfileResponse } from '../investment-robot/useGetRiskProfiles';
import { apifetch } from '../robotapi';
import { getModelPortfolioApiCall, GetModelPortfolioResponse } from './getModelPortfolio';

const getModelPortfoliosUrl = buildEndpoint(['riskprofiles'], [{ key: 'include', value: 'funds' }]);

export const getModelPortfoliosApiCall = apifetch<RiskProfileResponse>(getModelPortfoliosUrl, 'GET');

export const useGetModelPortfolios = () => {
    const getModelPortfoliosQuery = useQuery<Portfolio[]>(['portfolios', 'all'], async () => {
        const modelPortfolios = await getModelPortfoliosApiCall();
        const details = await Promise.allSettled(
            modelPortfolios.data.map(async (modelPortfolio) => {
                const factSheet = await getModelPortfolioApiCall(modelPortfolio.id + '')();
                return convertPortfolioResponseToPortfolio(modelPortfolio, factSheet);
            }),
        );

        return details
            .filter((p) => p.status === 'fulfilled')
            .map((p) => {
                return (p as PromiseFulfilledResult<Portfolio>).value;
            });
    });

    return getModelPortfoliosQuery;
};

const convertPortfolioResponseToPortfolio = (
    riskProfile: RiskProfileResponse['data'][0],
    factsheet: GetModelPortfolioResponse,
): Portfolio => {
    return {
        id: riskProfile.id + '',
        name: riskProfile.label,
        category: 'Portfolio',
        referenceDateCharacteristics: factsheet.referenceDateCharacteristics,
        basicCharacteristics: convertBasicCharacteristics(factsheet),
        riskCharacteristics: convertRiskCharacteristics(factsheet),
        investmentStrategyText: factsheet.investmentStrategyText,
        specifics: {
            bonds: convertBondSpecifics(factsheet),
            stocks: convertStockSpecifics(factsheet),
        },
        referenceDateHistoricalReturn: factsheet.referenceDateHistoricalReturn,
        historicalReturn: convertHistoricalReturns(factsheet),
        referenceDateQuality: factsheet.referenceDateCreditQuality,
        quality:
            factsheet.creditQualities?.map((q) => {
                return {
                    id: q.creditQualityId + '',
                    name: q.creditQualityName,
                    weight: q.creditQualityWeight * 100,
                };
            }) || [],
        referenceDateMaturity: factsheet.referenceDateMaturity,
        maturity:
            factsheet.maturities?.map((m) => {
                return {
                    id: m.maturityId + '',
                    name: m.maturityName,
                    weight: m.maturityWeight * 100,
                };
            }) || [],
        referenceDateSectors: {
            bonds: factsheet.referenceDateBondSectors,
            stocks: factsheet.referenceDateStockSectors,
        },
        sectors: {
            bonds:
                factsheet.bondSectors?.map((r) => {
                    return {
                        id: r.sectorId + '',
                        name: r.sectorName,
                        weight: r.sectorWeight * 100,
                    };
                }) || [],
            stocks: factsheet.stockSectors.map((r) => {
                return {
                    id: r.sectorId + '',
                    name: r.sectorName,
                    weight: r.sectorWeight * 100,
                };
            }),
        },
        referenceDateRegions: {
            bonds: factsheet.referenceDateBondRegions,
            stocks: factsheet.referenceDateStockRegions,
        },
        regions: {
            stocks:
                factsheet.stockRegions.map((m) => {
                    return {
                        id: m.regionId + '',
                        name: m.regionName,
                        weight: m.regionWeight * 100,
                    };
                }) || [],
            bonds: factsheet.bondRegions?.map((m) => {
                return {
                    id: m.regionId + '',
                    name: m.regionName,
                    weight: m.regionWeight * 100,
                };
            }),
        },
        assets: {
            stocks: riskProfile.funds
                .filter((f) => f.category === 'Stock')
                .map((f) => {
                    return { ...f, id: f.id + '', weight: f.weight * 100 };
                }),
            bonds: riskProfile.funds
                .filter((f) => f.category === 'Bond')
                .map((f) => {
                    return { ...f, id: f.id + '', weight: f.weight * 100 };
                }),
        },
    };
};

const convertBasicCharacteristics = (
    factSheetReponse: GetModelPortfolioResponse,
): Portfolio['basicCharacteristics'] => {
    return {
        startDate: factSheetReponse.basicCharacteristics.startDate,
        managementFee: factSheetReponse.basicCharacteristics.managementFee,
        portfolioManager: factSheetReponse.basicCharacteristics.modelPortfolioManager,
    };
};

const convertRiskCharacteristics = (factSheetReponse: GetModelPortfolioResponse): Portfolio['riskCharacteristics'] => {
    return {
        standardDeviation36M: factSheetReponse.riskCharacteristics.standarddeviation36M,
        sharpeRatio36M: factSheetReponse.riskCharacteristics.sharpeRatio36M,
    };
};

const convertStockSpecifics = (factSheetReponse: GetModelPortfolioResponse): Portfolio['specifics']['stocks'] => {
    if (!factSheetReponse.characteristicsUnderlyingStocks) return undefined;
    return {
        amountStocks: factSheetReponse.characteristicsUnderlyingStocks.numberOfStocks,
        averageMarketCapitalization: factSheetReponse.characteristicsUnderlyingStocks.averageMarketCapitalization,
        dividend: factSheetReponse.characteristicsUnderlyingStocks.dividend,
        rentabilityOwnAssets: factSheetReponse.characteristicsUnderlyingStocks.rentabilityOwnAssets,
        rateBookValueRatio: factSheetReponse.characteristicsUnderlyingStocks.rateBookValueRatio,
        rateProfitRatio: factSheetReponse.characteristicsUnderlyingStocks.rateProfitRatio,
    };
};

const convertBondSpecifics = (factSheetReponse: GetModelPortfolioResponse): Portfolio['specifics']['bonds'] => {
    if (!factSheetReponse.characteristicsUnderlyingBonds) return undefined;
    return {
        amountBonds: factSheetReponse.characteristicsUnderlyingBonds.numberOfBonds,
        yieldToMaturity: factSheetReponse.characteristicsUnderlyingBonds.yieldToMaturity,
        averageCouponRate: factSheetReponse.characteristicsUnderlyingBonds.averageCouponRate,
        averageDuration: factSheetReponse.characteristicsUnderlyingBonds.averageDuration,
        averageCreditQuality: factSheetReponse.characteristicsUnderlyingBonds.averageCreditQuality,
        averageWeightedDuration: factSheetReponse.characteristicsUnderlyingBonds.averageWeightedDuration,
    };
};

const convertHistoricalReturns = (factSheetReponse: GetModelPortfolioResponse): Portfolio['historicalReturn'] => {
    return factSheetReponse.historicalReturns.map((h) => {
        return {
            id: h.historicalReturnTypeId + '',
            beforeCosts: h.returnBeforeCosts * 100,
            afterCosts: h.returnAfterCosts * 100,
            description: h.description,
            benchmark: h.returnBenchmark * 100,
            year: h.year,
        };
    });
};
